import React, { useContext, useMemo, useState } from "react";
import "./FormCluster.css";
import FormInput from "../Form/FormInput";
import {
  sendOtpAPI,
  verifyOtpAPI,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import { CartContext } from "../../../../App";
import ReactGA from "react-ga4";
import FormDropdown from "../FormDropdown/FormDropdown";
import { motion } from "framer-motion";
import { isBundleLandingSchoolDetailsRequired } from "../../../../Utils/CommonMethods/bundleLandingHelpers";

// Validation patterns
const emailValidation = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
const mobileValidation = /^[1-9][0-9]{9}$/;
const dubaiMobileValidation = /^((\+971|971|0)?(50|51|52|54|55|56|58|4))\d{7}$/;

const otpValidation = /^\d{4}$/; // Regex for 4-digit number

type FiredEventsKeys =
  | "firstName"
  | "lastName"
  | "mobileNumber"
  | "email"
  | "schoolName";

const FormCluster2 = () => {
  const {
    userDetails,
    setUserDetails,
    backendSchool,
    isOtpVerified,
    setIsOtpVerified,
    nfoSelected,
  } = useContext(CartContext);

  const [firstName, setFirstName] = useState(
    sessionStorage.getItem("firstName") || ""
  );
  const [lastName, setLastName] = useState(
    sessionStorage.getItem("lastName") || ""
  );
  const [mobileNumber, setMobileNumber] = useState(
    sessionStorage.getItem("mobileNumber") || ""
  );
  const [email, setEmail] = useState(sessionStorage.getItem("email") || "");
  const [schoolName, setSchoolName] = useState(
    sessionStorage.getItem("schoolName") || ""
  );
  const [error, setError] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    sendOtpError: "",
    email: "",
    otp: "",
    schoolName: "",
    grade: "",
  });

  const [otp, setOtp] = useState("");
  const [isOtpFieldVisible, setIsOtpFieldVisible] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [grade, setGrade] = useState<number>(
    Number(sessionStorage.getItem("grade") || 0)
  );

  // Add new state to track whether events have been fired
  const [firedEvents, setFiredEvents] = useState({
    firstName: false,
    lastName: false,
    mobileNumber: false,
    email: false,
    schoolName: false,
  });

  const bundleLandingRequiresDetails = useMemo(
    () => isBundleLandingSchoolDetailsRequired(),
    []
  );

  // Helper function to fire GA event only once
  const fireGAEventOnce = (fieldName: FiredEventsKeys, action: string) => {
    if (!firedEvents[fieldName]) {
      ReactGA.event({
        category: "Form_Interaction",
        action: action,
        label: "Checkout_Form",
      });
      setFiredEvents((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  const sendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await sendOtpAPI(mobileNumber);
      if (response.status === "Success") {
        setIsOtpFieldVisible(true);
        setIsResendEnabled(false);
        setResendTimer(30);
        startResendTimer();
        setError((prevError) => ({
          ...prevError,
          sendOtpError: "",
        }));
        // GA event for OTP sent
        ReactGA.event({
          category: "OTP_Interaction",
          action: "OTP_Sent",
          label: "Checkout_Form",
        });
      } else {
        setError((prevError) => ({
          ...prevError,
          sendOtpError: "Error sending OTP, Please try again",
        }));
        // GA event for OTP send failure
        ReactGA.event({
          category: "OTP_Interaction",
          action: "OTP_Send_Failed",
          label: "Checkout_Form",
        });
      }
    } catch (err: any) {
      setError((prevError) => ({
        ...prevError,
        sendOtpError: "Error sending OTP, Please try again",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle OTP verification
  const verifyOtp = async () => {
    setIsLoading(true);
    console.log("verifyOtp");
    setError((prevError) => ({ ...prevError, otp: "" }));
    try {
      const response = await verifyOtpAPI(mobileNumber, otp);
      if (response.status === "Success") {
        setIsOtpVerified(true);
        setIsOtpFieldVisible(false);
        setError((prevError) => ({ ...prevError, otp: "" }));
        // GA event for successful OTP verification
        ReactGA.event({
          category: "OTP_Interaction",
          action: "OTP_Verified_Success",
          label: "Checkout_Form",
        });
      } else {
        setError((prevError) => ({
          ...prevError,
          otp: "Invalid OTP. Please try again.",
        }));
        // GA event for failed OTP verification
        ReactGA.event({
          category: "OTP_Interaction",
          action: "OTP_Verified_Failed",
          label: "Checkout_Form",
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError((prevError) => ({
        ...prevError,
        otp: "Invalid OTP. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle OTP actions
  const handleOtp = () => {
    if (!isOtpFieldVisible) {
      sendOtp();
    } else if (isResendEnabled) {
      handleResendOtp();
    }
  };

  // Function to start the resend timer
  const startResendTimer = () => {
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Function to handle resending OTP
  const handleResendOtp = () => {
    if (isResendEnabled) {
      // GA event for OTP resend
      ReactGA.event({
        category: "OTP_Interaction",
        action: "OTP_Resend",
        label: "Checkout_Form",
      });
      sendOtp();
    }
  };

  const nfoSelectedSession = sessionStorage.getItem("nfoSelected");
  const nfoSelectedBoolean = nfoSelectedSession === "true";
  const shouldCollectSchoolDetails =
    nfoSelected || nfoSelectedBoolean || bundleLandingRequiresDetails;

  // Form change handler
  const handleFormChange = (event: any) => {
    const { value, name } = event.target;

    let updatedError = { ...error };
    switch (name) {
      case "firstName":
        updatedError.firstName =
          value.length < 3 ? "Please enter a valid first name." : "";
        setFirstName(value);
        sessionStorage.setItem("firstName", value);
        setUserDetails({ ...userDetails, firstName: value });
        // Fire GA event only once when valid
        if (value.length >= 3) {
          fireGAEventOnce("firstName", "First_Name_Filled");
        }
        break;

      case "lastName":
        updatedError.lastName =
          value.length < 3 ? "Please enter a valid last name." : "";
        setLastName(value);
        sessionStorage.setItem("lastName", value);
        setUserDetails({ ...userDetails, lastName: value });
        if (value.length >= 3) {
          fireGAEventOnce("lastName", "Last_Name_Filled");
        }
        break;

      case "mobileNumber":
        const isValidMobile = backendSchool.isInternational
          ? dubaiMobileValidation.test(value)
          : mobileValidation.test(value);
        updatedError.mobileNumber = isValidMobile
          ? ""
          : "Please enter a valid mobile number.";
        setMobileNumber(value);
        sessionStorage.setItem("mobileNumber", value);
        setUserDetails({ ...userDetails, mobileNumber: value });
        fireGAEventOnce("mobileNumber", "Mobile_Number_Filled");
        break;

      case "email":
        updatedError.email = emailValidation.test(value)
          ? ""
          : "Please enter a valid email.";
        setEmail(value);
        sessionStorage.setItem("email", value);
        setUserDetails({ ...userDetails, email: value });
        if (emailValidation.test(value)) {
          fireGAEventOnce("email", "Email_Filled");
        }
        break;

      case "schoolName":
        // Only validate and update if nfoSelected is true
        if (shouldCollectSchoolDetails) {
          updatedError.schoolName =
            value.length < 5 ? "Please enter a valid school name." : "";
          setSchoolName(value);
          sessionStorage.setItem("schoolName", value);
          setUserDetails({ ...userDetails, schoolName: value });
          if (value.length >= 5) {
            fireGAEventOnce("schoolName", "School_Name_Filled");
          }
        }
        break;

      default:
        break;
    }

    setError(updatedError);
  };

  return (
    <div className="parent-wrapper">
      <div className="child-wrapper">
        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length <= 0) {
              setError({
                ...error,
                firstName: "Please enter a valid first name.",
              });
            } else {
              setError({ ...error, firstName: "" });
              setUserDetails({ ...userDetails, firstName: value });
              // Fire GA event only once when valid
              if (value.length >= 3) {
                fireGAEventOnce("firstName", "First_Name_Filled");
              }
            }
          }}
          title="First Name *"
          value={firstName}
          onChange={handleFormChange}
          name="firstName"
          type="text"
          error={error.firstName}
        />
        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length <= 0) {
              setError({
                ...error,
                lastName: "Please enter a valid last name.",
              });
            } else {
              setError({ ...error, lastName: "" });
              setUserDetails({ ...userDetails, lastName: value });
              if (value.length >= 3) {
                fireGAEventOnce("lastName", "Last_Name_Filled");
              }
            }
          }}
          title="Last Name *"
          value={lastName}
          onChange={handleFormChange}
          name="lastName"
          type="text"
          error={error.lastName}
        />
        <FormInput
          loading={isLoading}
          isVisible={true}
          disabled={isOtpVerified}
          onBlur={(event: any) => {
            const { value } = event?.target;
            const isValidMobile = backendSchool.isInternational
              ? dubaiMobileValidation.test(value)
              : mobileValidation.test(value);

            if (!isValidMobile) {
              setError({
                ...error,
                mobileNumber: "Please enter a valid mobile number.",
              });
            } else {
              setError({ ...error, mobileNumber: "" });
              setUserDetails({ ...userDetails, mobileNumber: mobileNumber });
              fireGAEventOnce("mobileNumber", "Mobile_Number_Filled");
            }
          }}
          title="Mobile Number *"
          value={mobileNumber}
          onChange={handleFormChange}
          name="mobileNumber"
          type="name"
          onShowVerified={isOtpVerified}
          error={error.mobileNumber}
        />
        {error.sendOtpError && (
          <div style={{ marginBottom: "10px" }} className="error-message">
            {error.sendOtpError}
          </div>
        )}
        {!error.mobileNumber && mobileNumber && !isOtpVerified && (
          <button
            className="otp-button"
            onClick={handleOtp}
            disabled={isOtpFieldVisible && !isResendEnabled}
          >
            {isOtpFieldVisible
              ? `Resend OTP ${isResendEnabled ? "" : `(${resendTimer}s)`}`
              : "Verify"}
          </button>
        )}
        {isOtpFieldVisible && (
          <>
            <FormInput
              isVisible={true}
              loading={isLoading}
              disabled={false}
              onBlur={() => {}}
              title="Enter OTP"
              value={otp}
              onChange={(e: any) => {
                const value = e.target.value;
                setOtp(value);
                setIsOtpValid(otpValidation.test(value)); // Validate OTP
              }}
              name="otp"
              type="text"
              error={error.otp}
            />
            <button
              className="otp-button"
              onClick={verifyOtp}
              disabled={!isOtpValid}
            >
              Verify OTP
            </button>
          </>
        )}

        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (!emailValidation.test(value)) {
              setError({ ...error, email: "Please enter a valid email." });
            } else {
              setError({ ...error, email: "" });
              setUserDetails({ ...userDetails, email: email });
              fireGAEventOnce("email", "Email_Filled");
            }
          }}
          title="Email *"
          value={email}
          onChange={handleFormChange}
          name="email"
          type="name"
          error={error.email}
        />
        {shouldCollectSchoolDetails && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <FormDropdown
              error={error.grade}
              setIsVisible={(value: boolean) => {
                setIsVisible(value);
                console.log(value);
              }}
              isVisible={isVisible}
              title="Grade *"
              value={grade}
              setValue={(value: number) => {
                if (value < 5 || value > 12) {
                  setError({
                    ...error,
                    grade: "Please enter a valid grade between 5 - 12.",
                  });
                } else {
                  setError({ ...error, grade: "" });
                  setGrade(value);
                  sessionStorage.setItem("grade", value.toString());
                  setUserDetails({ ...userDetails, grade: value });
                }
              }}
            />
            <div className="form-description">
              Please enter the applicable grade for academic year 2025-26
            </div>

            <FormInput
              isVisible={shouldCollectSchoolDetails}
              disabled={false}
              onBlur={(event: any) => {
                const { value } = event?.target;
                if (shouldCollectSchoolDetails) {
                  if (value.length < 5) {
                    setError({
                      ...error,
                      schoolName: "Please enter a valid school name.",
                    });
                  } else {
                    setError({ ...error, schoolName: "" });
                    setUserDetails({ ...userDetails, schoolName: value });
                    if (value.length >= 5) {
                      fireGAEventOnce("schoolName", "School_Name_Filled");
                    }
                  }
                }
              }}
              title="School Name *"
              value={schoolName}
              onChange={handleFormChange}
              name="schoolName"
              type="text"
              error={shouldCollectSchoolDetails ? error.schoolName : ""}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FormCluster2;
